// components
import { Tooltip, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

// hooks
import { useTranslation } from 'react-i18next';

// styles
import {
  StyledEntityColorIndicator,
  StyledOpenRequestFunctionBox
} from '../hooks/useGetColumns.styles';
import { IconContainer } from 'components/shared/Header/Header.styles';
import { StyledBoxFlexSmallGap } from 'components/shared/Box/Box.styles';

// types
import type { GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

/** One status a request row can be in: shown as an icon, with its own label as the icon title. */
type RowDisplayStatus = {
  condition: boolean;
  label: string;
  icon: IconType;
};

/**
 * The function name of an open request row, with an icon per status the request is in.
 *
 * The tooltip deliberately does not enumerate those statuses: it says "send to flex" when the
 * request was sent and "roster match" otherwise, and it only opens when at least one status holds.
 */
const OpenRequestFunctionCell = ({
  params
}: {
  params: GridRenderCellParams;
}) => {
  'use memo';

  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan'
  });

  const {
    functionColor,
    isSendToFlex,
    isRosterMatch,
    isParked,
    isSelf,
    isOpenForSelfMatch
  } = params.row;

  const displayStatus: RowDisplayStatus[] = [
    {
      condition: isParked,
      label: t('dataGrid.tooltip.rowIsParked'),
      icon: 'ban'
    },
    {
      condition: isSendToFlex,
      label: t('dataGrid.tooltip.sendToFlex'),
      icon: 'arrow-right-arrow-left'
    },
    {
      condition: isRosterMatch,
      label: t('dataGrid.tooltip.rosterMatch'),
      icon: 'message'
    },
    {
      condition: isSelf,
      label: t('dataGrid.tooltip.self'),
      icon: 'link'
    },
    {
      condition: isOpenForSelfMatch,
      label: t('dataGrid.tooltip.openForSelfMatch'),
      icon: 'users'
    }
  ];

  const isAnyStatus = displayStatus.some((status) => status.condition);

  const tooltipMessage = isSendToFlex
    ? t('dataGrid.tooltip.sendToFlex')
    : t('dataGrid.tooltip.rosterMatch');

  return (
    <Tooltip title={tooltipMessage} disableHoverListener={!isAnyStatus} arrow>
      <StyledOpenRequestFunctionBox>
        {functionColor && (
          <StyledEntityColorIndicator functionColor={functionColor} />
        )}
        <Typography variant="body2">{params.value}</Typography>
        <StyledBoxFlexSmallGap>
          {displayStatus.map((status) =>
            status.condition ? (
              <IconContainer
                key={status.label}
                iconSize="SMALL"
                iconColor="inherit"
                title={status.label}
              >
                <Icon type={status.icon} color="inherit" />
              </IconContainer>
            ) : null
          )}
        </StyledBoxFlexSmallGap>
      </StyledOpenRequestFunctionBox>
    </Tooltip>
  );
};

export default OpenRequestFunctionCell;
